import { useEffect, useState } from "react";
import { useParams, Link, useSearchParams } from "react-router-dom";
import { ArrowLeft, LineChart, Loader2 } from "lucide-react";
import api from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../components/ui/tabs";
import OverviewTab from "./tabs/OverviewTab";
import ElegibilidadTab from "./tabs/ElegibilidadTab";
import BorrowingBaseTab from "./tabs/BorrowingBaseTab";
import CovenantsTab from "./tabs/CovenantsTab";
import WaterfallTab from "./tabs/WaterfallTab";
import ReportesTab from "./tabs/ReportesTab";
import FuentesTab from "./tabs/FuentesTab";
import FinancieroTab from "./tabs/FinancieroTab";
import RevolventeTab from "./tabs/RevolventeTab";
import AnalyticsTab from "./tabs/AnalyticsTab";
import StressTestTab from "./tabs/StressTestTab";

const ESTADO_BADGE = {
    ratificada: "badge-gold",
    disponible: "badge-gold",
    dispuesta: "badge-exito",
    amortizando: "badge-gold",
    pagada: "badge-exito",
    en_default: "badge-error",
    reestructurada: "badge-mock",
};

function money(n, cur = "MXN") {
    return Number(n || 0).toLocaleString("es-MX", { style: "currency", currency: cur, maximumFractionDigits: 0 });
}

export default function FacilityDetail() {
    const { id } = useParams();
    const [searchParams, setSearchParams] = useSearchParams();
    const initialTab = searchParams.get("tab") || "overview";
    const [fac, setFac] = useState(null);
    const [loading, setLoading] = useState(true);
    const [tab, setTab] = useState(initialTab);
    const [error, setError] = useState(null);

    const changeTab = (v) => {
        setTab(v);
        setSearchParams({ tab: v });
    };

    const load = async () => {
        setLoading(true);
        setError(null);
        try {
            const { data } = await api.get(`/facilities/${id}`);
            setFac(data);
        } catch (err) {
            setError(err?.response?.data?.detail || "Facility no encontrada");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

    if (loading) {
        return (
            <div className="p-10 flex items-center gap-3 text-muted-foreground text-sm" data-testid="facility-detail-loading">
                <Loader2 className="h-4 w-4 animate-spin" /> Cargando facility…
            </div>
        );
    }

    if (error || !fac) {
        return (
            <div className="p-10" data-testid="facility-detail-error">
                <Link to="/v1/facilities">
                    <Button variant="ghost"><ArrowLeft className="h-4 w-4 mr-2" /> Volver</Button>
                </Link>
                <div className="mt-6 text-sm text-[color:var(--brand-coral)]">{error}</div>
            </div>
        );
    }

    return (
        <div className="p-8 lg:p-10 max-w-[1600px] space-y-6" data-testid="facility-detail-page">
            <div>
                <Link to="/v1/facilities" className="inline-flex items-center gap-2 text-xs text-muted-foreground hover:text-foreground mb-3">
                    <ArrowLeft className="h-3 w-3" /> Facilities
                </Link>
                <div className="text-[11px] uppercase tracking-[0.3em] text-[color:var(--brand-dark-gold)] font-semibold mb-2">
                    V1 · Garantía · {fac.tipo}
                </div>
                <div className="flex items-baseline gap-4 flex-wrap">
                    <h1 className="font-display text-4xl tracking-tight flex items-center gap-3">
                        <LineChart className="h-8 w-8 text-[color:var(--brand-dark-gold)]" />
                        {fac.numero}
                    </h1>
                    <span className={ESTADO_BADGE[fac.estado] || "badge-gold"}>{fac.estado}</span>
                </div>
                <p className="text-sm text-muted-foreground mt-1">{fac.nombre_comercial}</p>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div className="kpi-card">
                    <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Moneda</div>
                    <div className="font-display text-2xl mt-1">{fac.moneda}</div>
                </div>
                <div className="kpi-card">
                    <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Comprometido</div>
                    <div className="font-display text-2xl mt-1">{money(fac.monto_comprometido, fac.moneda)}</div>
                </div>
                <div className="kpi-card">
                    <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Dispuesto</div>
                    <div className="font-display text-2xl mt-1">{money(fac.monto_dispuesto, fac.moneda)}</div>
                </div>
                <div className="kpi-card">
                    <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Vencimiento</div>
                    <div className="font-display text-2xl mt-1">{fac.fecha_vencimiento}</div>
                </div>
            </div>

            <Tabs value={tab} onValueChange={changeTab}>
                <TabsList className="flex-wrap h-auto" data-testid="facility-tabs">
                    <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
                    <TabsTrigger value="elegibilidad" data-testid="tab-elegibilidad">Elegibilidad</TabsTrigger>
                    <TabsTrigger value="borrowing-base" data-testid="tab-borrowing-base">Borrowing Base</TabsTrigger>
                    <TabsTrigger value="covenants" data-testid="tab-covenants">Covenants</TabsTrigger>
                    <TabsTrigger value="waterfall" data-testid="tab-waterfall">Waterfall</TabsTrigger>
                    <TabsTrigger value="reportes" data-testid="tab-reportes">Reportes lender</TabsTrigger>
                    <TabsTrigger value="fuentes" data-testid="tab-fuentes">Fuentes de datos</TabsTrigger>
                    <TabsTrigger value="financiero" data-testid="tab-financiero">Financiero (A3)</TabsTrigger>
                    <TabsTrigger value="revolvente" data-testid="tab-revolvente">Revolvente (A5)</TabsTrigger>
                    <TabsTrigger value="analytics" data-testid="tab-analytics">Analytics (A6)</TabsTrigger>
                    <TabsTrigger value="stress" data-testid="tab-stress">Stress test (A6)</TabsTrigger>
                </TabsList>

                <TabsContent value="overview" className="mt-6">
                    <OverviewTab facility={fac} reload={load} />
                </TabsContent>
                <TabsContent value="elegibilidad" className="mt-6">
                    <ElegibilidadTab facility={fac} />
                </TabsContent>
                <TabsContent value="borrowing-base" className="mt-6">
                    <BorrowingBaseTab facility={fac} />
                </TabsContent>
                <TabsContent value="covenants" className="mt-6">
                    <CovenantsTab facility={fac} />
                </TabsContent>
                <TabsContent value="waterfall" className="mt-6">
                    <WaterfallTab facility={fac} />
                </TabsContent>
                <TabsContent value="reportes" className="mt-6">
                    <ReportesTab facility={fac} />
                </TabsContent>
                <TabsContent value="fuentes" className="mt-6">
                    <FuentesTab facility={fac} />
                </TabsContent>
                <TabsContent value="financiero" className="mt-6">
                    <FinancieroTab facility={fac} />
                </TabsContent>
                <TabsContent value="revolvente" className="mt-6">
                    <RevolventeTab facility={fac} />
                </TabsContent>
                <TabsContent value="analytics" className="mt-6">
                    <AnalyticsTab facility={fac} />
                </TabsContent>
                <TabsContent value="stress" className="mt-6">
                    <StressTestTab facility={fac} />
                </TabsContent>
            </Tabs>
        </div>
    );
}
